import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaAd, FaSync, FaEye, FaTimes, FaCheckCircle, FaPlay, FaExternalLinkAlt, FaPaperPlane, FaArrowLeft } from "react-icons/fa";
import AdsSubNav from '../../Common/AdsSubNav';

const CompletedFreeAds = () => {
  const navigate = useNavigate();
  const [completedFreeAds, setCompletedFreeAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedProof, setExpandedProof] = useState(null);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState("");

  // Fetch completed free ads from API
  const fetchCompletedFreeAds = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/completed/free/ads`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Response of Completed Free Ads:", response);
      setCompletedFreeAds(response.data.data || []);
    } catch (err) {
      setError("Failed to fetch completed free ads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompletedFreeAds();
  }, []);

  const formatScreenshotUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http")
      ? url
      : `${process.env.REACT_APP_API_BASE_URL}/${url.replace(/\\/g, "/")}`;
  };


  // Sort ads by submittedAt (newest first)
  const sortedAds = [...completedFreeAds].sort((a, b) => {
    const dateA = new Date(a.submittedAt || a.proofDetails?.submittedAt);
    const dateB = new Date(b.submittedAt || b.proofDetails?.submittedAt);
    return dateB - dateA;
  });

  // Helper functions
  const handleViewScreenshot = (screenshot) => {
    if (screenshot) {
      // Handle both Cloudinary URLs and local paths
      const imageUrl = screenshot.startsWith('http')
        ? screenshot
        : `${process.env.REACT_APP_API_BASE_URL}/${screenshot.replace(/\\/g, '/')}`;
      setScreenshotUrl(imageUrl);
      setShowScreenshotModal(true);
    }
  };

  const toggleProofDetails = (adId) => {
    setExpandedProof(expandedProof === adId ? null : adId);
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(sortedAds.length / rowsPerPage);
  const paginatedAds = sortedAds.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Helper to format time as hh:mm:ss AM/PM
  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, "0");
    return `${formattedHours}:${minutes}:${seconds} ${ampm}`;
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pb-12">
      {/* Modern Header - Compact for Mobile */}
      <div className="max-w-7xl mx-auto mb-3 sm:mb-6 px-2 sm:px-4 mt-2 sm:mt-6">
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-700 rounded-xl sm:rounded-2xl shadow-xl p-2 sm:p-6 text-white relative overflow-hidden">
          {/* Decorative background element */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />

          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[9px] font-black uppercase tracking-tight border border-white/20 transition-all whitespace-nowrap shadow-sm"
              >
                ← Back
              </button>
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <div className="p-2 sm:p-3 bg-white/20 rounded-lg sm:rounded-xl backdrop-blur-md border border-white/30 hidden xs:flex">
                  <FaAd className="text-sm sm:text-2xl text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-sm sm:text-2xl md:text-3xl font-black tracking-tight truncate">Completed Reward Ads</h1>
                  <p className="text-violet-100/80 text-[9px] sm:text-sm font-bold uppercase tracking-wider mt-0.5 truncate">Successfully delivered & rewarded</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="bg-white/20 backdrop-blur-md rounded-lg sm:rounded-xl p-2 sm:px-4 sm:py-3 border border-white/30 text-center flex-1 sm:flex-none min-w-[70px] sm:min-w-[100px] shadow-lg">
                <div className="text-sm sm:text-2xl font-black">{completedFreeAds.length}</div>
                <div className="text-[8px] sm:text-xs font-black uppercase tracking-widest text-violet-100">Finished</div>
              </div>
              <button
                onClick={fetchCompletedFreeAds}
                disabled={loading}
                className="p-2.5 sm:p-4 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-all border border-white/30 backdrop-blur-md text-white shadow-lg active:scale-95 group flex items-center justify-center"
              >
                <FaSync className={`w-3 h-3 sm:w-4 sm:h-4 ${loading ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdsSubNav type="free" />

      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
        {/* Mobile View - Compact Cards */}
        <div className="sm:hidden space-y-4">
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="h-32 bg-white rounded-2xl border border-slate-100 animate-pulse shadow-sm" />
            ))
          ) : sortedAds.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100 shadow-sm px-6 mt-4">
              <FaAd className="text-4xl text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-black text-slate-900 uppercase">No Completed Ads</h3>
              <p className="text-slate-400 text-sm mt-1">When tasks are finalized, they will appear here.</p>
            </div>
          ) : (
            sortedAds.map((ad, index) => (
              <div key={index} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex h-32 transform transition-all active:scale-[0.98] ring-1 ring-slate-50">
                {/* Left: Media Preview */}
                <div className="w-28 bg-slate-50 flex-shrink-0 relative overflow-hidden">
                  {ad.proofDetails?.screenshot ? (
                    <img
                      src={formatScreenshotUrl(ad.proofDetails.screenshot)}
                      alt="Proof"
                      className="w-full h-full object-cover"
                      onClick={() => handleViewScreenshot(ad.proofDetails.screenshot)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <FaAd className="text-2xl opacity-10" />
                    </div>
                  )}

                  {/* Badge Overlay */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase bg-violet-600 text-white shadow-lg ring-1 ring-white/20">
                      PAID
                    </span>
                  </div>
                </div>

                {/* Right: Details Section */}
                <div className="flex-1 p-3 flex flex-col justify-between min-w-0 text-left">
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-start gap-1">
                      <h3 className="text-slate-900 font-black text-[10px] uppercase tracking-tight truncate leading-tight flex-1">
                        {ad.adDetails?.adType || "Reward Ad"}
                      </h3>
                      <span className="text-emerald-600 font-black text-[9px] bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 uppercase tracking-tighter">
                        Success
                      </span>
                    </div>

                    <div className="space-y-1 mt-1">
                      <p className="text-slate-400 text-[8px] font-bold uppercase tracking-tight truncate font-mono bg-slate-50 px-1.5 py-0.5 rounded w-fit">
                        ID: {(ad.adDetails?._id || ad._id || "").slice(-8).toUpperCase()}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black text-indigo-500 uppercase">{ad.proofDetails?.platform || "---"}</span>
                        <span className="w-1 h-1 bg-slate-200 rounded-full" />
                        <span className="text-[9px] font-bold text-slate-400">
                          {new Date(ad.proofDetails?.submittedAt || ad.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => toggleProofDetails(ad._id)}
                    className="w-full py-2 bg-slate-900 hover:bg-slate-800 text-white text-[8px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-1.5 transition-all shadow-md shadow-slate-200"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop View - Table */}
        <div className="hidden sm:block overflow-hidden bg-white rounded-2xl border border-slate-100 shadow-xl overflow-x-auto mt-6">
          <table className="min-w-full text-left border-collapse">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Media</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Ad Info</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Location</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Timeline</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">Status</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-50">
              {sortedAds.map((ad, idx) => (
                <React.Fragment key={idx}>
                  <tr className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-slate-200 group-hover:shadow-md transition-all">
                        {ad.proofDetails?.screenshot ? (
                          <img
                            src={formatScreenshotUrl(ad.proofDetails.screenshot)}
                            alt="Proof"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onClick={() => handleViewScreenshot(ad.proofDetails.screenshot)}
                          />
                        ) : (
                          <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-300">
                            <FaAd />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-black text-slate-900">{ad.adDetails?.adType || "Reward Ad"}</span>
                        <span className="text-[10px] font-mono font-bold text-slate-400 mt-0.5 uppercase tracking-tighter">ID: {ad.adDetails?._id || ad._id || "---"}</span>
                        <span className="text-[9px] font-black text-indigo-500 mt-1 uppercase tracking-widest">{ad.proofDetails?.platform || "---"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col text-xs text-slate-600 font-bold uppercase tracking-tight">
                        <span>{ad.adDetails?.city || "---"}</span>
                        <span className="text-[10px] text-slate-400 font-medium">{ad.adDetails?.state || "---"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col text-[10px] font-bold">
                        <div className="flex justify-between items-center gap-2 mb-1">
                          <span className="text-slate-400 uppercase tracking-widest">Work Submitted</span>
                          <span className="text-slate-900">{new Date(ad.proofDetails?.submittedAt || ad.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-slate-400 uppercase tracking-widest">Ad Created</span>
                          <span className="text-slate-900">{new Date(ad.adDetails?.createdAt || ad.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-[10px] font-black rounded-full border border-emerald-100 uppercase tracking-widest">
                        <FaCheckCircle className="text-emerald-500" />
                        Confirmed
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-slate-400">
                      <button
                        onClick={() => toggleProofDetails(ad._id)}
                        className="p-3 hover:bg-slate-100 rounded-xl transition-all active:scale-95 border border-transparent hover:border-slate-200"
                      >
                        <FaEye className={expandedProof === ad._id ? "text-indigo-600" : ""} />
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Row Detail */}
                  {expandedProof === ad._id && (
                    <tr className="bg-indigo-50/30">
                      <td colSpan="6" className="px-6 py-0 animate-fadeIn overflow-hidden">
                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6 border-x-4 border-indigo-600/20 bg-white/50 my-2 rounded-2xl shadow-inner text-left">
                          <div className="space-y-4">
                            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Work Proof</p>
                            {ad.proofDetails?.videoLink ? (
                              <a href={ad.proofDetails.videoLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white rounded-xl border border-indigo-100 text-blue-600 hover:text-blue-800 transition-all shadow-sm">
                                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 text-blue-500"><FaExternalLinkAlt /></div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] font-black text-indigo-900 uppercase">Video Link</p>
                                  <p className="text-xs truncate font-medium">{ad.proofDetails.videoLink}</p>
                                </div>
                              </a>
                            ) : (
                              <div className="p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200 text-center text-slate-400 text-xs font-bold uppercase tracking-widest">No Link Provided</div>
                            )}
                          </div>

                          <div className="space-y-4">
                            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Metadata</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="p-3 bg-white rounded-xl border border-indigo-100 shadow-sm">
                                <p className="text-[9px] font-black text-slate-400 uppercase">Channel</p>
                                <p className="text-xs font-bold text-slate-900 truncate">{ad.proofDetails?.channelName || "---"}</p>
                              </div>
                              <div className="p-3 bg-white rounded-xl border border-indigo-100 shadow-sm">
                                <p className="text-[9px] font-black text-slate-400 uppercase">Duration</p>
                                <p className="text-xs font-bold text-slate-900 truncate">{ad.proofDetails?.duration || "---"}</p>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Full Screenshot</p>
                            <div className="relative group cursor-zoom-in rounded-xl overflow-hidden border-2 border-indigo-100 shadow-lg" onClick={() => handleViewScreenshot(ad.proofDetails?.screenshot)}>
                              <img src={formatScreenshotUrl(ad.proofDetails?.screenshot)} alt="Full Proof" className="w-full h-32 object-cover" />
                              <div className="absolute inset-0 bg-indigo-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"><FaEye size={24} /></div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Improved Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-8 pb-10">
            <nav className="inline-flex items-center gap-2 bg-white p-2 rounded-2xl shadow-lg border border-slate-100">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2.5 rounded-xl border border-slate-100 text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-all"
              >
                <FaArrowLeft size={12} />
              </button>
              <div className="px-4 py-2 bg-violet-50 rounded-xl border border-violet-100 flex items-center gap-2">
                <span className="text-[10px] font-black text-violet-400 uppercase tracking-widest">Page</span>
                <span className="text-sm font-black text-violet-700">{currentPage}</span>
                <span className="text-[10px] font-black text-violet-400 uppercase tracking-widest">of</span>
                <span className="text-sm font-black text-violet-700">{totalPages}</span>
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2.5 rounded-xl border border-slate-100 text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-all rotate-180"
              >
                <FaArrowLeft size={12} />
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Improved Screenshot Modal */}
      {showScreenshotModal && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fadeIn" onClick={() => setShowScreenshotModal(false)}>
          <div className="relative max-w-5xl w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <div className="absolute -top-12 right-0 flex items-center gap-4">
              <button onClick={() => setShowScreenshotModal(false)} className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all ring-1 ring-white/20">
                <FaTimes />
              </button>
            </div>
            <img src={screenshotUrl} alt="Proof Full Size" className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl ring-4 ring-white/5" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletedFreeAds;
